import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Check as CheckIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import * as testimonialApi from '../../services/testimonial';

interface SnackbarState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

const TestimonialsManagement: React.FC = () => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [pendingTestimonials, setPendingTestimonials] = useState<any[]>([]);
  const [approvedTestimonials, setApprovedTestimonials] = useState<any[]>([]);
  const [rejectedTestimonials, setRejectedTestimonials] = useState<any[]>([]);

  // Dialog states
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedTestimonial, setSelectedTestimonial] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    loadTestimonials();
  }, []);

  const loadTestimonials = async () => {
    try {
      setLoading(true);
      const [pendingData, approvedData, rejectedData] = await Promise.all([
        testimonialApi.getAllTestimonials({ status: 'pending' }),
        testimonialApi.getAllTestimonials({ status: 'approved' }),
        testimonialApi.getAllTestimonials({ status: 'rejected' })
      ]);

      setPendingTestimonials(pendingData.testimonials);
      setApprovedTestimonials(approvedData.testimonials);
      setRejectedTestimonials(rejectedData.testimonials);
    } catch (error) {
      console.error('Error loading testimonials:', error);
      showSnackbar('Failed to load testimonials', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleApprove = async (id: string) => {
    try {
      await testimonialApi.approveTestimonial(id);
      showSnackbar('Testimonial approved successfully');
      loadTestimonials();
    } catch (error) {
      console.error('Error approving testimonial:', error);
      showSnackbar('Failed to approve testimonial', 'error');
    }
  };

  const handleReject = async () => {
    if (!selectedTestimonial) return;
    try {
      await testimonialApi.rejectTestimonial(selectedTestimonial.id, rejectionReason);
      showSnackbar('Testimonial rejected');
      setRejectDialogOpen(false);
      setRejectionReason('');
      setSelectedTestimonial(null);
      loadTestimonials();
    } catch (error) {
      console.error('Error rejecting testimonial:', error);
      showSnackbar('Failed to reject testimonial', 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;

    try {
      await testimonialApi.deleteTestimonial(id);
      showSnackbar('Testimonial deleted successfully');
      loadTestimonials();
    } catch (error) {
      console.error('Error deleting testimonial:', error);
      showSnackbar('Failed to delete testimonial', 'error');
    }
  };

  const renderTestimonialsTable = (testimonials: any[], showActions: boolean = true, status: string) => (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>User</TableCell>
            <TableCell>Title</TableCell>
            <TableCell>Content</TableCell>
            <TableCell>Date</TableCell>
            {status === 'rejected' && <TableCell>Reason</TableCell>}
            {showActions && <TableCell align="right">Actions</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {testimonials.length === 0 ? (
            <TableRow>
              <TableCell colSpan={status === 'rejected' ? 6 : 5} align="center">
                <Typography color="text.secondary" sx={{ py: 4 }}>
                  No {status} testimonials
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            testimonials.map((testimonial) => (
              <TableRow key={testimonial.id}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {testimonial.user?.firstName} {testimonial.user?.lastName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {testimonial.user?.organizationName}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{testimonial.title}</Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 400,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical'
                    }}
                  >
                    {testimonial.content}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption">
                    {new Date(testimonial.createdAt).toLocaleDateString()}
                  </Typography>
                </TableCell>
                {status === 'rejected' && (
                  <TableCell>
                    <Typography variant="caption" color="error">
                      {testimonial.rejectionReason || 'No reason provided'}
                    </Typography>
                  </TableCell>
                )}
                {showActions && (
                  <TableCell align="right">
                    {status === 'pending' && (
                      <>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleApprove(testimonial.id)}
                          sx={{ mr: 1 }}
                        >
                          <CheckIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setSelectedTestimonial(testimonial);
                            setRejectDialogOpen(true);
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </>
                    )}
                    {status !== 'pending' && (
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDelete(testimonial.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '70vh' }}>
        <CircularProgress size={60} />
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          mb: 4,
          p: 4,
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.secondary.main, 0.8)})`,
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <ReviewIcon sx={{ fontSize: 40 }} />
          <Box>
            <Typography variant="h4" fontWeight={700}>
              Testimonials Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9, mt: 0.5 }}>
              Review, approve, reject and manage user testimonials
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Stats Cards */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
        <Card sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h4" fontWeight={700} color="warning.main">
              {pendingTestimonials.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Pending Review</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h4" fontWeight={700} color="success.main">
              {approvedTestimonials.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Approved</Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: 1, borderRadius: 2 }}>
          <CardContent>
            <Typography variant="h4" fontWeight={700} color="error.main">
              {rejectedTestimonials.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">Rejected</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Tabs */}
      <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          textColor="primary"
          indicatorColor="primary"
          sx={{
            borderBottom: `1px solid ${theme.palette.divider}`,
            '& .MuiTab-root': {
              minHeight: 64,
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              fontSize: '1rem'
            }
          }}
        >
          <Tab
            label={`Pending (${pendingTestimonials.length})`}
            sx={{ color: theme.palette.warning.main }}
          />
          <Tab
            label={`Approved (${approvedTestimonials.length})`}
            sx={{ color: theme.palette.success.main }}
          />
          <Tab
            label={`Rejected (${rejectedTestimonials.length})`}
            sx={{ color: theme.palette.error.main }}
          />
        </Tabs>

        <Box sx={{ p: 3 }}>
          {tabValue === 0 && renderTestimonialsTable(pendingTestimonials, true, 'pending')}
          {tabValue === 1 && renderTestimonialsTable(approvedTestimonials, true, 'approved')}
          {tabValue === 2 && renderTestimonialsTable(rejectedTestimonials, true, 'rejected')}
        </Box>
      </Paper>

      {/* Reject Dialog */}
      <Dialog
        open={rejectDialogOpen}
        onClose={() => setRejectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Testimonial</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to reject this testimonial? You can optionally provide a reason.
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Rejection Reason (optional)"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleReject}>
            Reject
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default TestimonialsManagement;
